<template>
    <div
        ref="inputWrapper"
        :class="[
            'dt-input-wrapper',
            { 'is-ai': isAI === true },
            { 'has-stt': type === 'text' && speechToText === true },
            {
                'reduce-border-radius':
                    type === 'text' &&
                    autoHeight === true &&
                    adjustedHeight > defaultHeight,
            },
        ]"
    >
        <textarea
            v-if="type === 'text' && autoHeight === true"
            :id="id"
            ref="textarea"
            :class="['dt-input', { error: isError }]"
            rows="1"
            :placeholder="placeholder"
            :required="internalRequired"
            :data-payload="fullPayload"
            v-model="value"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"  
        ></textarea>
        <input
            v-if="type === 'text' && autoHeight === false"
            :id="id"
            :type="type"
            :class="['dt-input', { error: isError }]"
            :placeholder="placeholder"
            :required="internalRequired"
            :data-payload="fullPayload"
            v-model="value"
            @input="handleInput"
            @focus="handleFocus"
            @blur="handleBlur"  
        />
        <button
            v-if="type === 'text' && speechToText === true"
            class="speech-to-text"
            @click="handleSpeechToText"
        ></button>
        <div class="input-ai-style"></div>
        <img
            v-if="isAI"
            src="@/assets/customizer_icon_magic_wand.svg"
            class="magic-wand-icon"
            width="22"
            height="22"
            alt="Zauberstab Icon"
        />
    </div>
</template>

<script>
import { reportingFetch } from "../utils/httpReporting";
import { nextTick } from "vue";
import EventBus from "../event-bus";
import { trackEvent } from "../utils/tracking";
import { safeGetJson, safeSetJson } from "../utils/safeStorage";

export default {
    name: "DTInput",
    props: {
        id: {
            type: String,
            default: "",
        },
        type: {
            type: String,
            default: "text",
            validator: function (value) {
                return ["text"].includes(value);
            },
            required: true,
        },
        payload: {
            type: String,
            required: true,
        },
        sharePayload: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: "",
        },
        autoHeight: {
            type: Boolean,
            default: false,
        },
        speechToText: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: false,
        },
        sendImmediately: {
            type: Boolean,
            default: false,
        },
        backendUrl: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isAI: false,
            isError: false,
            fullPayload: this.payload,
            internalRequired: this.required,
            value: "",
            originalValue: "",
            defaultHeight: 0,
            adjustedHeight: 0,
            currentBookId: "unknown_book",
            currentStep: "unknown_step",
        };
    },
    inject: ["pageSettings"],
    mounted() {
        if (this.$route) {
            this.currentBookId = this.$route.params?.bookId || "unknown_book";
            this.currentStep = this.$route.name || "unknown_step";
        }


        requestAnimationFrame(() => {
            this.$nextTick(() => {
                if (this.autoHeight === true && this.type === "text") {
                    EventBus.on("tabChanged", this.adjustHeight);
                    EventBus.on("stepThreeShowContent", this.adjustHeight);
                    this.adjustHeight();
                    window.addEventListener("resize", this.adjustHeight);
                }

                EventBus.on(
                    "throwErrorForRequiredInputs",
                    this.handleErrorState
                );
                this.fullPayload = this.getFullPayload();
                this.loadFromLocalStorage();
            });

            if (this.autoHeight === true && this.type === "text") {
                if (document.fonts) {
                    document.fonts.ready.then(() => {
                        this.adjustHeight();
                    });
                } else {
                    window.addEventListener("load", this.adjustHeight);
                }
            }
        });
    },
    beforeUnmount() {
        if (this.autoHeight === true && this.type === "text") {
            EventBus.off("tabChanged", this.adjustHeight);
            EventBus.off("stepThreeShowContent", this.adjustHeight);
            window.removeEventListener("resize", this.adjustHeight);

            if (document.fonts) {
                // document.fonts.ready is a one-shot promise and has no listener to remove.
            } else {
                window.removeEventListener("load", this.adjustHeight);
            }
        }

        EventBus.off("throwErrorForRequiredInputs", this.handleErrorState);
    },
    watch: {
        value() {
            if (this.autoHeight && this.type === "text") {
                this.adjustHeight();
            }
        },
    },
    methods: {
        handleFocus() {
            this.handleScrollToOnFocus();
            this.originalValue = this.value;
        },
        
        handleBlur() {
            if (this.value.trim() !== "" && this.value !== this.originalValue) {
                if (document.readyState === 'loading') {
                    document.addEventListener('DOMContentLoaded', () => this.sendCompletionEvent(), { once: true });
                } else {
                    this.sendCompletionEvent();
                }
                
                if (this.sendImmediately && this.backendUrl) {
                    this.sendToBackend();
                }
            }
        },
        
        async sendToBackend() {
            if (!this.backendUrl || this.backendUrl.trim() === "") {
                return;
            }

            try {
                const payload = {
                    id: this.id,
                    payload: this.fullPayload,
                    value: this.value,
                    bookId: this.currentBookId,
                    step: this.currentStep
                };

                const storageKeys = [
                    this.pageSettings.storageKey,
                    this.pageSettings.step1StorageKey,
                    this.pageSettings.step2StorageKey,
                    this.pageSettings.step3StorageKey
                ].filter(Boolean);
                
                storageKeys.forEach(key => {
                    payload[key] = safeGetJson(key, {});
                });

                const response = await reportingFetch(this.backendUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                }, { feature: "input_backend" });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
            } catch (error) {
                // sentry-ignore: reportingFetch already reported the failure (feature input_backend)
                if (process.env.VUE_APP_DEV_MODE === "true") {
                    console.error("Error sending input to backend", {
                        code: error?.code || "unknown",
                        status: error?.response?.status || "none",
                    });
                }
            }
        },
        
        sendCompletionEvent() {
            trackEvent(this, {
                event: "input_interaction_complete",
                category: "Customizer",
                action: "CompletedInput",
                label: this.placeholder, 
                input_id: this.id,
                input_type: this.type,
                input_payload: this.fullPayload,
                value_length: this.value.length,
                has_content: this.value.trim() !== "",
                input_value: this.value
            });
        },
        
        handleScrollToOnFocus() {
        },

        getFullPayload() {
            if (!this.payload || this.payload.trim() === "") {
                return null;
            }

            let element = this.$el;

            while (element && element.parentElement) {
                if (element.parentElement.classList.contains("tab-content")) {
                    const prefix = element.parentElement.getAttribute(
                        "data-payload-prefix"
                    );
                    if (prefix) {
                        return `${prefix}.${this.payload}`;
                    }
                }

                if (element.parentElement.classList.contains("milestone")) {
                    return `milestones.${this.payload}`;
                }

                element = element.parentElement;
            }

            return this.payload;
        },
        loadFromLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let current = stepValues;
            let sharedCurrent = sharedValues;

            for (let i = 0; i < keys.length; i++) {
                if (!current[keys[i]]) {
                    current[keys[i]] = i === keys.length - 1 ? {} : {};
                }
                current = current[keys[i]];

                if (!sharedCurrent[keys[i]]) {
                    sharedCurrent[keys[i]] = i === keys.length - 1 ? {} : {};
                }
                sharedCurrent = sharedCurrent[keys[i]];
            }

            if (this.sharePayload && sharedCurrent.value) {
                this.value = sharedCurrent.value;
                current.value = sharedCurrent.value;
            } else if (current.value) {
                this.value = current.value;
            } else {
                current.value = this.value === "" ? "" : this.value;
                this.value = current.value;
            }

            current.required = this.required;
            if (this.sharePayload) {
                sharedCurrent.value = this.value;
                sharedCurrent.required = this.required;
            }

            nextTick(() => {
                if (
                    this.$refs.inputWrapper &&
                    this.$refs.inputWrapper.style.display === "none"
                ) {
                    current.required = false;
                }
            });

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        saveToLocalStorage() {
            if (!this.fullPayload || !this.pageSettings.storageKey) {
                return;
            }

            const stepValues = safeGetJson(this.pageSettings.storageKey, {});
            const sharedValues = safeGetJson("sharedCustomizerValues", {});
            const keys = this.fullPayload.split(".");
            let currentStep = stepValues;
            let currentShared = sharedValues;

            for (let i = 0; i < keys.length - 1; i++) {
                if (!currentStep[keys[i]]) {
                    currentStep[keys[i]] = {};
                }
                currentStep = currentStep[keys[i]];

                if (this.sharePayload) {
                    if (!currentShared[keys[i]]) {
                        currentShared[keys[i]] = {};
                    }
                    currentShared = currentShared[keys[i]];
                }
            }

            currentStep[keys[keys.length - 1]] = {
                ...currentStep[keys[keys.length - 1]],
                value: this.value,
                required: this.internalRequired,
            };

            if (this.sharePayload) {
                currentShared[keys[keys.length - 1]] = {
                    ...currentShared[keys[keys.length - 1]],
                    value: this.value,
                    required: this.internalRequired,
                };
            }

            safeSetJson(this.pageSettings.storageKey, stepValues);
            if (this.sharePayload) {
                safeSetJson("sharedCustomizerValues", sharedValues);
            }
        },
        handleInput(event) {
            this.isAI = false;
            this.isError = false;
            this.value = event.target.value;

            this.saveToLocalStorage();
        },
        handleErrorState(newState) {
            if (newState === false) {
                this.isError = false;
                return;
            }

            if (this.internalRequired && this.value.trim() === "") {
                this.isError = true;
            }
        },
        adjustHeight() {
            const textarea = this.$refs.textarea;

            if (!textarea) return;

            requestAnimationFrame(() => {
                nextTick(() => {
                    const currentScrollPos = window.scrollY;

                    let isTempValue = false;
                    if (!textarea.value) {
                        textarea.value = textarea.placeholder || " ";
                        isTempValue = true;
                    }

                    if (textarea.scrollHeight > 0) {
                        if (this.defaultHeight === 0) {
                            this.defaultHeight = textarea.offsetHeight;
                        }

                        textarea.style.height = "auto";
                        textarea.style.overflow = "hidden";
                        textarea.style.height = `${textarea.scrollHeight}px`;

                        this.adjustedHeight = textarea.scrollHeight;
                        window.scrollTo({ top: currentScrollPos });
                    } else {
                        setTimeout(this.adjustHeight, 100);
                    }

                    if (isTempValue) {
                        textarea.value = "";
                    }
                });
            });
        },
        handleSpeechToText() {
            EventBus.emit("openPopup", "popup-speech-to-text");
        },
        setAIValue(newValue) {
            this.isAI = true;

            let i = 0;
            this.value = "";
            const interval = setInterval(() => {
                if (i < newValue.length) {
                    this.value += newValue.charAt(i);
                    i++;
                } else {
                    clearInterval(interval);
                    this.saveToLocalStorage();
                }
            }, 25);
        },
        setRequired(newState) {
            this.internalRequired = newState;
            this.saveToLocalStorage();
        },
        resetValue() {
            this.value = "";
            this.isError = false;
            this.isAI = false;
            this.saveToLocalStorage();
        },
    },
};
</script>

<style lang="scss" scoped>
@use "@/assets/customizer.mixins.scss" as *;
.dt-input-wrapper {
    position: relative;
    width: 100%;

    .dt-input {
        position: relative;
        z-index: 3;
        width: 100%;
        padding: 16px 32px;
        border: 2px solid $color-light-gray;
        border-radius: 46px;
        background-color: $color-white;
        transition: border-color 0.3s;
        @include font-body-m;

        & {
            color: $color-input-active-text;
        }

        @media (max-width: $breakpoint-tablet) {
            padding: 16px 24px;
            border-radius: 36px;
            font-size: 16px;
        }

        &:active,
        &:focus {
            border-width: 2px;
            border-color: #625f6b;
            outline: none;
            box-shadow: none;
        }

        &::placeholder {
            color: $color-placeholder-text;
        }

        &.error {
            border-color: $color-input-error;
        }
    }

    textarea {
        resize: none;
    }

    .input-ai-style {
        position: absolute;
        z-index: 1;
        display: none;
        top: -2px;
        right: -3px;
        bottom: 1px;
        left: -3px;
        border-radius: 46px;
        background: $ai-gradient;

        @media (max-width: $breakpoint-tablet) {
            border-radius: 36px;
        }

        &::before {
            content: "";
            position: absolute;
            z-index: 2;
            top: 2px;
            right: 2px;
            bottom: 2px;
            left: 2px;
            border-radius: 46px;
            background: $color-white;

            @media (max-width: $breakpoint-tablet) {
                border-radius: 36px;
            }
        }
    }

    .speech-to-text {
        position: absolute;
        z-index: 3;
        top: 24px;
        right: 24px;
        width: 11px;
        height: 18px;
        border: none;
        background-color: transparent;
        background-image: url("~@/assets/customizer_icon_mic.svg");
        background-size: contain;
        background-position: center;
        background-repeat: no-repeat;
        cursor: pointer;

        @media (min-width: $breakpoint-tablet) {
            display: none;
        }
    }

    .magic-wand-icon {
        position: absolute;
        z-index: 4;
        top: 17px;
        right: 24px;
    }

    &.has-stt {
        .dt-input {
            padding-right: 42px;
        }
    }

    &.is-ai {
        .dt-input {
            padding-right: 58px;
            border-color: transparent;
            background: $ai-gradient;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            box-shadow: none;
        }

        .input-ai-style {
            display: block;
        }
    }

    &.reduce-border-radius {
        .dt-input,
        .input-ai-style,
        .input-ai-style::before {
            border-radius: 36px;

            @media (max-width: $breakpoint-tablet) {
                border-radius: 20px !important;
            }
        }
    }
}
</style>
